import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, Tab, Tabs, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

// Components
import withLocalization from '~/hoc/withLocalization';
import Button from '~/components/CustomButton/CustomButton.jsx';
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';
import MessageTab from './MessageTab';
import VariablesTab from './VariablesTab';
import SettingsTab from './SettingsTab';

// Elements
import { formatKey } from '~/utils/commonMethods';
import { NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS } from '~/utils/constants';
import { getUnknownTokens, normalizeEmailHtml } from './templateUtils';

const PRIORITY_OPTIONS = ['HIGH', 'MEDIUM', 'LOW'];

const EMPTY_VARIABLES = [];

const DEFAULT_FORM = {
    key: '',
    category: '',
    description: '',
    is_active: true,
    allow_push: true,
    allow_email: true,
    allow_system: true,
    priority: 'MEDIUM',
    is_batch_notification: false,
    is_message_customizable: true,
    variables: [],
    push_title: '',
    push_message: '',
    email_title: '',
    email_message: '',
};

const EditNotificationModal = ({ show, onHide, notification, t, notificationStore, commonStore, hideSettings, isCreate, initialMessages, onSaveMessages, onCreated }) => {
    const [form, setForm] = useState(DEFAULT_FORM);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState(hideSettings ? 'push' : 'settings');
    const initialFormRef = useRef(DEFAULT_FORM);

    const pushMsg = notification?.messages?.find((m) => m.type === 'PUSH');
    const emailMsg = notification?.messages?.find((m) => m.type === 'EMAIL');

    useEffect(() => {
        if (isCreate) {
            const next = {
                ...DEFAULT_FORM,
                key: '',
                category: '',
                description: '',
                variables: [],
                is_active: true,
                allow_push: false,
                allow_email: false,
                allow_system: false,
                is_batch_notification: false,
                is_message_customizable: false,
            };
            initialFormRef.current = next;
            setForm(next);
            setActiveTab('settings');
            return;
        }
        if (!notification) return;
        const customPush = hideSettings && initialMessages?.push;
        const customEmail = hideSettings && initialMessages?.email;

        const variablesByKey = new Map();
        (pushMsg?.variables || []).forEach((v) => variablesByKey.set(v.key, v));
        (emailMsg?.variables || []).forEach((v) => variablesByKey.set(v.key, v));

        const next = {
            is_active: notification.is_active,
            allow_push: notification.delivery_options?.allow_push ?? true,
            allow_email: notification.delivery_options?.allow_email ?? true,
            allow_system: notification.delivery_options?.allow_system ?? true,
            priority: notification.priority,
            is_batch_notification: notification.is_batch_notification,
            is_message_customizable: notification.is_batch_notification ? false : notification.is_message_customizable,
            push_title: customPush ? customPush.title : (pushMsg?.title || ''),
            push_message: customPush ? customPush.message : (pushMsg?.message || ''),
            email_title: customEmail ? customEmail.title : (emailMsg?.title || ''),
            email_message: customEmail ? customEmail.message : (emailMsg?.message || ''),
            key: notification.key || '',
            category: notification.category || '',
            description: notification.description || '',
            variables: Array.from(variablesByKey.values()),
        };
        initialFormRef.current = next;
        setForm(next);
        if (hideSettings) {
            const pushEnabled = next.allow_push || next.allow_system;
            setActiveTab(pushEnabled ? 'push' : next.allow_email ? 'email' : 'push');
        } else {
            setActiveTab('settings');
        }
    }, [notification, isCreate]);

    const isDirty = Object.keys(DEFAULT_FORM).some((key) => form[key] !== initialFormRef.current[key]);
    const noChannelsAvailable = hideSettings && !form.allow_push && !form.allow_system && !form.allow_email;

    const handleClose = () => {
        if (!isDirty) {
            onHide();
            return;
        }
        confirmAlert({
            title: t('Unsaved Changes!'),
            message: t('You have unsaved changes. Are you sure you want to close?'),
            buttons: [
                { label: t('Yes'), onClick: onHide },
                { label: t('No'), onClick: () => { } },
            ],
        });
    };

    const handleChange = useCallback((field, value) => {
        if ((field === 'allow_push' || field === 'allow_system') && value && !pushMsg) {
            commonStore.addNotification(t('The default message is not available for push/system notifications. Please add a custom message to ensure users receive the notification.'), null, 'warning', 'top-right', 5000);
        }
        if (field === 'allow_email' && value && !emailMsg) {
            commonStore.addNotification(t('The default message is not available for email notifications. Please add a custom message to ensure users receive the notification.'), null, 'warning', 'top-right', 5000);
        }

        setForm((prev) => {
            if (field === 'is_active' && !value) {
                return { ...prev, is_active: false, allow_push: false, allow_email: false, allow_system: false, is_batch_notification: false, is_message_customizable: false };
            }

            if (field === 'allow_push' && value) {
                return { ...prev, allow_push: true, allow_system: true };
            }
            if (field === 'allow_system' && !value) {
                return { ...prev, allow_system: false, allow_push: false };
            }
            if (field === 'is_batch_notification' && value) {
                return { ...prev, is_batch_notification: true, is_message_customizable: false };
            }
            return { ...prev, [field]: value };
        });
    }, [pushMsg, emailMsg, commonStore, t]);

    const priorityOptions = useMemo(() => PRIORITY_OPTIONS.map((p) => ({ label: t(p), value: p })), [t]);
    const categoryOptions = useMemo(() => NOTIFICATION_CATEGORIES.map((c) => ({ label: t(NOTIFICATION_CATEGORY_LABELS[c]), value: c })), [t]);

    const handleKeyChange = (e) => {
        handleChange('key', e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'));
    };

    const handleCreateSave = async () => {
        const needsPush = form.allow_push || form.allow_system;
        const needsEmail = form.allow_email;

        const pushUnknown = [...getUnknownTokens(form.push_title, form.variables), ...getUnknownTokens(form.push_message, form.variables)];
        const emailUnknown = [...getUnknownTokens(form.email_title, form.variables), ...getUnknownTokens(form.email_message, form.variables)];

        const validations = [
            { invalid: !form.key || !/^[A-Z][A-Z0-9_]*$/.test(form.key), message: t('Notification key must start with a letter and contain only uppercase letters, numbers, and underscores.') },
            { invalid: !form.category, message: t('Please select a category.') },
            { invalid: !form.description.trim(), message: t('Enter Description') },
            { invalid: needsPush && (!form.push_title || !form.push_message), message: t('Push title and message are required.') },
            { invalid: needsEmail && (!form.email_title || !form.email_message), message: t('Email title and message are required.') },
            { invalid: pushUnknown.length > 0, message: `${t('Push message')}: ${t('Unknown placeholder')}: ${pushUnknown.join(', ')}` },
            { invalid: emailUnknown.length > 0, message: `${t('Email message')}: ${t('Unknown placeholder')}: ${emailUnknown.join(', ')}` },
        ];
        const failed = validations.find((v) => v.invalid);
        if (failed) {
            commonStore.addNotification(failed.message, null, 'error');
            return;
        }

        setSaving(true);
        try {
            const response = await notificationStore.createMasterNotification({
                key: form.key,
                description: form.description,
                category: form.category,
                delivery_options: { allow_push: form.allow_push, allow_email: form.allow_email, allow_system: form.allow_system },
                priority: form.priority,
                is_batch_notification: form.is_batch_notification,
                is_message_customizable: form.is_message_customizable,
                variables: form.variables,
                push: needsPush ? { title: form.push_title, message: form.push_message } : null,
                email: needsEmail ? { title: form.email_title, message: normalizeEmailHtml(form.email_message) } : null,
            });
            await notificationStore.loadMasterNotifications(true);
            notificationStore.setLastInteractedId(response?.data?.id);
            if (onCreated) onCreated(form.category);
            setSaving(false);
            commonStore.addNotification(t('Saved'), null, 'success');
            onHide();
        } catch (e) {
            setSaving(false);
            commonStore.addNotification(e?.message || t('Something went wrong. Please try again.'), null, 'error');
        }
    };

    const handleSave = async () => {
        if (!notification) return;

        if (isCreate) {
            await handleCreateSave();
            return;
        }

        const pushUnknown = [...getUnknownTokens(form.push_title, pushMsg?.variables), ...getUnknownTokens(form.push_message, pushMsg?.variables)];
        const emailUnknown = [...getUnknownTokens(form.email_title, emailMsg?.variables), ...getUnknownTokens(form.email_message, emailMsg?.variables)];
        if (pushUnknown.length > 0 || emailUnknown.length > 0) {
            const parts = [];
            if (pushUnknown.length > 0) parts.push(`${t('Push message')}: ${pushUnknown.join(', ')}`);
            if (emailUnknown.length > 0) parts.push(`${t('Email message')}: ${emailUnknown.join(', ')}`);
            commonStore.addNotification(`${t('The following variables are not recognized')}: ${parts.join(' | ')}`, null, 'error');
            return;
        }

        const normalizedEmailMessage = normalizeEmailHtml(form.email_message);

        setSaving(true);
        try {
            if (hideSettings && onSaveMessages) {
                const pushData = (form.allow_push || form.allow_system) ? { title: form.push_title, message: form.push_message } : null;
                const emailData = form.allow_email ? { title: form.email_title, message: normalizedEmailMessage } : null;
                await onSaveMessages(pushData, emailData);
                initialFormRef.current = { ...form, email_message: normalizedEmailMessage };
            } else {
                await notificationStore.updateMasterNotification(notification.id, {
                    is_active: form.is_active,
                    delivery_options: { allow_push: form.allow_push, allow_email: form.allow_email, allow_system: form.allow_system },
                    priority: form.priority,
                    is_batch_notification: form.is_batch_notification,
                    is_message_customizable: form.is_message_customizable,
                });
                const saveMasterMessage = (existingMsg, type, data) => {
                    if (existingMsg) return notificationStore.updateMasterMessage(existingMsg.id, data);
                    if (!data.title || !data.message) return null;
                    return notificationStore.createMasterMessage({ notification_master_id: notification.id, type, ...data });
                };

                await Promise.all([
                    saveMasterMessage(pushMsg, 'PUSH', { title: form.push_title, message: form.push_message, variables: form.variables }),
                    saveMasterMessage(emailMsg, 'EMAIL', { title: form.email_title, message: normalizedEmailMessage, variables: form.variables }),
                ]);
                await notificationStore.loadMasterNotifications(true);
                initialFormRef.current = { ...form, email_message: normalizedEmailMessage };
            }
            setForm((prev) => ({ ...prev, email_message: normalizedEmailMessage }));
            setSaving(false);
            commonStore.addNotification(t('Saved'), null, 'success');
            onHide();
        } catch (e) {
            setSaving(false);
            commonStore.addNotification(e?.message || t('Something went wrong. Please try again.'), null, 'error');
        }
    };

    return (
        <Modal className="modal right fade filter modal_revised" show={show} onHide={handleClose}>
            <Modal.Header>
                <Modal.Title>{isCreate ? t('Add Notification') : (notification ? formatKey(notification.key) : '')}</Modal.Title>
                <img
                    src={CloseModalIcon}
                    className="cursor-pointer"
                    alt="close icon"
                    onClick={handleClose}
                />
            </Modal.Header>

            <Modal.Body>
                {notification && noChannelsAvailable && (
                    <div className="d-flex flex-column align-items-center text-center text-muted mt-4 py-4">
                        <i className="fa fa-lock fa-2x mb-3" aria-hidden="true"></i>
                        {t('Push and Email notifications are disabled for this notification. There are no settings to customize.')}
                    </div>
                )}
                {notification && !noChannelsAvailable && (
                    <div className="control-panel">
                        <Tabs activeKey={activeTab} onSelect={(tabKey) => setActiveTab(tabKey)} id="EditNotificationTabs" className="mt-2">
                            {!hideSettings && (
                                <Tab eventKey="settings" title={t('Settings')}>
                                    <SettingsTab
                                        t={t}
                                        form={form}
                                        isCreate={isCreate}
                                        onChange={handleChange}
                                        onKeyChange={handleKeyChange}
                                        categoryOptions={categoryOptions}
                                        priorityOptions={priorityOptions}
                                    />
                                </Tab>
                            )}

                            {!hideSettings && (
                                <Tab
                                    eventKey="variables"
                                    disabled={!pushMsg && !emailMsg}
                                    title={
                                        (!pushMsg && !emailMsg) ? (
                                            <OverlayTrigger placement="top" overlay={<Tooltip id="variables-tab-disabled-tooltip">{t('Configure a push or email notification message to enable variables.')}</Tooltip>}>
                                                <span>{t('Variables')}</span>
                                            </OverlayTrigger>
                                        ) : t('Variables')
                                    }
                                >
                                    <VariablesTab t={t} variables={form.variables} onChange={(vars) => handleChange('variables', vars)} />
                                </Tab>
                            )}

                            <Tab eventKey="push" title={`${t('Push')}/${t('System')} ${t('Message')}`} disabled={!form.allow_push && !form.allow_system}>
                                <MessageTab
                                    t={t}
                                    titleField="push_title"
                                    messageField="push_message"
                                    titleValue={form.push_title}
                                    messageValue={form.push_message}
                                    onChange={handleChange}
                                    variables={pushMsg?.variables || EMPTY_VARIABLES}
                                />
                            </Tab>

                            <Tab eventKey="email" title={`${t('Email')} ${t('Message')}`} disabled={!form.allow_email}>
                                <MessageTab
                                    t={t}
                                    titleField="email_title"
                                    messageField="email_message"
                                    titleValue={form.email_title}
                                    messageValue={form.email_message}
                                    onChange={handleChange}
                                    variables={emailMsg?.variables || EMPTY_VARIABLES}
                                    isEmail
                                    allowRawHtml={!hideSettings}
                                />
                            </Tab>
                        </Tabs>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <div className="d-flex justify-content-end gap-2">
                    <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
                        {t('Cancel')}
                    </Button>
                    {!noChannelsAvailable && (
                        <Button fill dynamic_lg onClick={handleSave} disabled={saving || !isDirty}>
                            {saving ? t('Saving...') : t('Save')}
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
};


export default inject('notificationStore', 'commonStore')(withLocalization(observer(EditNotificationModal)));
